import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  DataTable,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Pagination,
  InlineLoading,
  InlineNotification,
  Tag,
  Grid,
  Column,
  DatePicker,
  DatePickerInput,
  Dropdown,
  Button,
} from "@carbon/react";
import { Filter } from "@carbon/icons-react";
import { callApi } from "../../../services/apiService";

const headers = [
  { key: "propertySeqId", header: "Property Id" },
  { key: "propertyTitle", header: "Property Title / Type" },
  { key: "userName", header: "User name " },
  { key: "verificationStatus", header: "Verification Status" },
  { key: "status", header: " Status" },
  { key: "amount", header: "Amount" },
  { key: "paymentMethod", header: "Payment Method" },
  { key: "transactionType", header: "Transaction Type" },  
  { key: "planName", header: "Plan Name" },
  { key: "createdAt", header: "Created At" },
];

// Helper function to get status tag type
const getStatusTagType = (status) => {
  switch (status?.toLowerCase()) {
    case "verified":
      return "green";
    case "pending":
      return "yellow";
    case "rejected":
      return "red";
    case "active":
      return "blue";
    default:
      return "gray";
  }
};

// Format currency
const formatCurrency = (amount, currency = "INR") => {
  if (!amount && amount !== 0) return `${currency} 0.00`;

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Format date time
const formatDateTime = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr; // fallback if invalid
  
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// Valid tag types for Carbon components
const VALID_TAG_TYPES = ["red", "magenta", "purple", "blue", "cyan", "teal", "green", "gray", "cool-gray", "warm-gray", "high-contrast", "outline"];

// Helper function to get valid tag type
const getValidTagType = (type) => {
  return VALID_TAG_TYPES.includes(type) ? type : "gray";
};

const attrMapping = {
  transactionStatus: "subscription_status",
};
export default function TransactionReport() {
  const mountedRef = useRef(true);
  const [rows, setRows] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Pagination and Search states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  // Filter states
  const [planFilter, setPlanFilter] = useState(null);
  //const [planStatusFilter, setPlanStatusFilter] = useState(null);

  //const [planStatusOptions, setPlanStatusOptions] = useState([]);
  
  const [transactionStatusFilter, setTransactionStatusFilter] = useState(null);
  const [transactionStatusOptions, setTransactionStatusOptions] = useState([]);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState(null);
  const [transactionTypeOptions, setTransactionTypeOptions] = useState([]);
  // Dynamic filter options
  const [planOptions, setPlanOptions] = useState([]);
  
const [startDate, setStartDate] = useState(null);
const [endDate, setEndDate] = useState(null);


  // ------------------ Search Logic ------------------
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim().toLowerCase()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

   // Fetch Attribute Values
const fetchAttributesValue = useCallback(async () => {
  if (!mountedRef.current) return;

  setLoading(true);
  setError("");

  try {
    const response = await callApi("GET", "/attribute-values/");
    const data = Array.isArray(response?.data) ? response.data : [];

    const formattedRows = data.map((item) => ({
      id: item.id,
      attributeValue: item.attr_val_name,
      attributeSpec: item.atr_spec_name,
      attributeName: item.attr_name,
      attributeSpecId: item.atr_spec_id,
      attributeId: item.atr_id,
    }));

    // Dynamic attribute mapping options
    Object.entries(attrMapping).forEach(([stateKey, attrName]) => {
      const options = formattedRows
        .filter(
          (item) =>
            item.attributeName?.toLowerCase() ===
            attrName.toLowerCase()
        )
        .map((item) => ({
          id: item.attributeValue,
          text: item.attributeValue,
        }));

      // Remove duplicates
      const uniqueOptions = Array.from(
        new Map(options.map((item) => [item.id, item])).values()
      );

      // Set dropdown options
      if (stateKey === "transactionStatus") {
        setTransactionStatusOptions(uniqueOptions);
      }

    });

    if (!mountedRef.current) return;

  } catch (error) {
   
    setError("Failed to load attribute values.");
  } finally {
    if (mountedRef.current) {
      setLoading(false);
    }
  }
}, []);
  // Normalize Transaction Report Data
  const normalizeTransactionData = useCallback((item, index) => ({
id: String(
  item.PROPERTY_TRANSACTION_ID ||
  item.subscription_details?.USER_SUBSCRIPTION_ID ||
  `${item.USER_ID}-${item.CREATED_AT}`
),
   propertyUuid: item.PROPERTY_UUID,
    propertyTitle: item.PROPERTY_TITLE,
    propertySeqId: item.PROPERTY_SEQ_ID,
    propertyType: item.PROPERTY_TYPE,
    userName: item.user_name,
    userEmail: item.user_email,
    transactionType: item.TRANSACTION_TYPE,
    amount: item.AMOUNT || 0,
    verificationStatus: item.VERIFICATION_STATUS || "N/A",
    currency: item.CURRENCY || "INR",
    status: item.STATUS,
    description: item.DESCRIPTION,
    createdAt: item.CREATED_AT,
    paymentMethod: item.PAYMENT_METHOD || "N/P",
    paymentGateway: item.PAYMENT_GATEWAY || "N/P",
    planName: item.PLAN_NAME, 
    planCode: item.SUBSCRIPTION_PLAN_ID,  
    subscriptionStatus: item.subscription_details?.SUBSCRIPTION_STATUS,
    durationDays: item.subscription_details?.DURATION_DAYS || null,
    isSubscription: item.is_subscription_only || false,
    originalData: item,
  }), []);

  // Fetch Transaction Report Data
  const fetchTransactionReport = useCallback(async () => {
    if (!mountedRef.current) return;
    
    setLoading(true);
    setError("");
    try {
      const response = await callApi("GET", "/transactions/reports/all-transactions");
      console.log("Transactions API:", response.data.transactions);
console.log("First Transaction:", response.data.transactions[0]);
      
      if (response?.status_code === 200 && response?.data) {
        const properties = response.data.transactions || [];
        const normalizedData = properties.map(normalizeTransactionData);
        setRows(normalizedData);
        const typeOptions = [
  ...new Set(
    properties
      .map(item => item.TRANSACTION_TYPE)
      .filter(Boolean)
  )
].map(type => ({
  id: type,
  text: type,
}));

setTransactionTypeOptions(typeOptions);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      
      setError("Failed to load transaction report. Please try again.");
      setRows([]);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [normalizeTransactionData]);

  const fetchSubscriptionPlans = useCallback(async () => {
    try {
      const res = await callApi("GET", "/subscription-plans/");

      if (res?.status_code === 200 && Array.isArray(res.data)) {
        const planOpts = [];
        //const statusSet = new Set();

       res.data.forEach((plan) => {
  if (plan.name && plan.code) {
    planOpts.push({
      id: plan.name,
      text: plan.name,
    });
  }

 // if (plan.status) {
   // statusSet.add(plan.status);
  //}
});

        //const statusOpts = Array.from(statusSet).map(status => ({
          //id: status,
          //text: status
        //}));

        setPlanOptions(planOpts);
        //setPlanStatusOptions(statusOpts);

        //setPlanFilter(null);
        //setPlanStatusFilter(null);
      }
    } catch (err) {
      
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    fetchTransactionReport();
    fetchSubscriptionPlans(); 
    fetchAttributesValue();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchTransactionReport, fetchSubscriptionPlans,fetchAttributesValue]);

  // Reset all filters
const resetFilters = () => {
  setPlanFilter(null);
  //setPlanStatusFilter(null);
  setTransactionTypeFilter(null);
  setTransactionStatusFilter(null);

  setStartDate(null);
  setEndDate(null);

  setSearchTerm("");
  setDebouncedSearch("");
  
  setPage(1);

  fetchTransactionReport();
  
};
const formatDate = (date) => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};
const applyFilters = async () => {
  if (!mountedRef.current) return;

  setLoading(true);
  setError("");

  try {
    const queryParams = new URLSearchParams();

    if (planFilter?.id) {
      queryParams.append("subscription_plan_name", planFilter.id);
    }
    
    if (transactionTypeFilter?.id) {
    queryParams.append(
      "transaction_types",
      transactionTypeFilter.id
      );
    }


    if (transactionStatusFilter?.id) {
      queryParams.append(
        "STATUS",
        transactionStatusFilter.id
      );
    }

if (startDate) {
  queryParams.append("start_date", formatDate(startDate));
}

if (endDate) {
  queryParams.append("end_date", `${formatDate(endDate)} 23:59:59`);
}

    if (debouncedSearch) {
      queryParams.append("search", debouncedSearch);
    }

    const response = await callApi(
      "GET",
      `/transactions/reports/all-transactions?${queryParams.toString()}`
    );
    console.log("Start Date Object:", startDate);
console.log("End Date Object:", endDate);

console.log("Formatted Start:", formatDate(startDate));
console.log("Formatted End:", formatDate(endDate));

console.log("Request URL:", queryParams.toString());

    if (response?.status_code === 200 && response?.data) {
const transactions = response.data.transactions || [];

let normalizedData = transactions.map(normalizeTransactionData);


if (planFilter?.id) {
  normalizedData = normalizedData.filter(
    (item) =>
      item.planName?.trim().toLowerCase() ===
      planFilter.id.trim().toLowerCase()
  );
}

setRows(normalizedData);
setPage(1);
    } else {
      setRows([]);
    }
  } catch (error) {
    
    setError("Failed to load filtered transactions.");
    setRows([]);
  } finally {
    setLoading(false);
  }
};


const filteredRows = useMemo(() => {
  if (!debouncedSearch) {
    return rows;
  }

  const search = debouncedSearch.toLowerCase();

  return rows.filter((row) =>
    row.propertyTitle?.toLowerCase().includes(search) ||
    row.propertyType?.toLowerCase().includes(search) ||
    row.userName?.toLowerCase().includes(search) ||
    row.verificationStatus?.toLowerCase().includes(search) ||
    row.transactionType?.toLowerCase().includes(search) ||
    row.planName?.toLowerCase().includes(search) ||
    row.status?.toLowerCase().includes(search)
  );
}, [rows, debouncedSearch]);

  
  const paginatedRows = useMemo(() =>
    filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize]
  );
useEffect(() => {
  setPage(1);
}, [rows]);

  // Count active filters
  const activeFilterCount = [
    planFilter && planFilter.id !== "all",
   // planStatusFilter,
    transactionTypeFilter,
    transactionStatusFilter,
    !!debouncedSearch
  ].filter(Boolean).length;

  const getVerificationStatusTag = (status) => {
    const statusMap = {
      "Pending": { type: "yellow", text: "Pending" },
      "Verified": { type: "green", text: "Verified" },
      "Rejected": { type: "red", text: "Rejected" },
      "Approved": { type: "blue", text: "Approved" },
    };
    const config = statusMap[status] || { type: "gray", text: status || "Unknown" };
    // Use getValidTagType to ensure the type is valid
    return <Tag type={getValidTagType(config.type)}>{config.text}</Tag>;
  };

  

  return (
    <>
      {/* Filter Section - Above Table Container */}
      <div style={{ 
        padding: "1rem", 
        backgroundColor: "#f4f4f4", 
        borderRadius: "8px", 
        marginBottom: "1rem",
        border: "1px solid #e0e0e0"
      }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}>
          <Filter style={{ marginRight: "0.5rem" }} />
          <h4 style={{ margin: 0, flex: 1 }}>Filter Transactions</h4>

        </div>
        
        <Grid style={{ padding: 0 }}>
          {/* PLAN */}
          <Column lg={4} md={4} sm={4}>
            <Dropdown
              id="plan-filter"
              items={planOptions}
              titleText="Plan Name"
              label="Choose"
              itemToString={(item) => item?.text || ""}
              selectedItem={planFilter}
              onChange={({ selectedItem }) => setPlanFilter(selectedItem)}
              size="sm"
            />
          </Column>
          
          <Column lg={4} md={4} sm={4}>
          <Dropdown
            id="transaction-type-filter"
            items={transactionTypeOptions}
            titleText="Transaction Type"
            label="Choose"
            itemToString={(item) => item?.text || ""}
            selectedItem={transactionTypeFilter}
            onChange={({ selectedItem }) =>
              setTransactionTypeFilter(selectedItem)
            }
            size="sm"
          />
        </Column>
          {/* STATUS */}


          <Column lg={4} md={4} sm={4}>
          <Dropdown
            id="transaction-status-filter"
            items={transactionStatusOptions}
            titleText="Transaction Status"
            label="Choose"
            itemToString={(item) => item?.text || ""}
            selectedItem={transactionStatusFilter}
            onChange={({ selectedItem }) =>
              setTransactionStatusFilter(selectedItem)
            }
            size="sm"
          />
</Column>
<Column lg={4} md={4} sm={4}>
  <DatePicker
    datePickerType="single"
    value={startDate}
    onChange={(dates) => setStartDate(dates[0])}
  >
    <DatePickerInput
      id="start-date"
      labelText="Start Date"
      placeholder="dd/mm/yyyy"
      size="sm"
    />
  </DatePicker>
</Column>

<Column lg={4} md={4} sm={4}>
  <DatePicker
    datePickerType="single"
    value={endDate}
    onChange={(dates) => setEndDate(dates[0])}
  >
    <DatePickerInput
      id="end-date"
      labelText="End Date"
      placeholder="dd/mm/yyyy"
      size="sm"
    />
  </DatePicker>
</Column>
        </Grid>
        <div
  style={{
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "1rem",
    gap: "0.75rem",
  }}
>
  <Button kind="secondary" onClick={resetFilters}>
    Reset
  </Button>

  <Button onClick={applyFilters}>
    Search
  </Button>
</div>
<div
  style={{
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
    marginTop: "1rem",
  }}
>
  {planFilter && (
    <Tag
      type="blue"
      filter
      onClose={() => setPlanFilter(null)}
    >
      Plan: {planFilter.text}
    </Tag>
  )}

  {transactionTypeFilter && (
  <Tag
    type="purple"
    filter
    onClose={() => setTransactionTypeFilter(null)}
  >
    Type: {transactionTypeFilter.text}
  </Tag>
)}
  

  {transactionStatusFilter && (
    <Tag
      type="cyan"
      filter
      onClose={() => setTransactionStatusFilter(null)}
    >
      Transaction: {transactionStatusFilter.text}
    </Tag>
  )}

  {startDate && (
    <Tag
      type="teal"
      filter
      onClose={() => setStartDate(null)}
    >
      Start: {new Date(startDate).toLocaleDateString()}
    </Tag>
  )}

  {endDate && (
    <Tag
      type="teal"
      filter
      onClose={() => setEndDate(null)}
    >
      End: {new Date(endDate).toLocaleDateString()}
    </Tag>
  )}

  {searchTerm && (
    <Tag
      type="magenta"
      filter
      onClose={() => {
        setSearchTerm("");
        setDebouncedSearch("");
      }}
    >
      Search: {searchTerm}
    </Tag>
  )}
</div>
      </div>

      {/* DataTable Container */}
      <DataTable
  key={JSON.stringify(paginatedRows.map((r) => r.id))}
  rows={paginatedRows}
  headers={headers}
>
  {({
    rows,
    headers,
    getHeaderProps,
    getRowProps,
    getTableProps,
  }) => {
    return (
            <TableContainer title="Transaction Report">
              <TableToolbar>
                <TableToolbarContent>
                  <TableToolbarSearch
                    expanded
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by property title, type, user, or verification status..."
                    value={searchTerm}
                  />
                </TableToolbarContent>
              </TableToolbar>

              {/* Loading and Error Messages */}
              {loading && (
                <div style={{ padding: "2rem", textAlign: "center" }}>
                  <InlineLoading description="Loading transaction report..." />
                </div>
              )}

              {error && !loading && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="error"
                    title="Error"
                    subtitle={error}
                    lowContrast
                    onClose={() => setError("")}
                  />
                </div>
              )}

              {success && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="success"
                    title="Success"
                    subtitle={success}
                    lowContrast
                    onClose={() => setSuccess("")}
                  />
                </div>
              )}

              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map((header) => {
                      const headerProps = getHeaderProps({ header });
                      const { key: headerKey, ...restHeaderProps } = headerProps;
                      return (
                        <TableHeader 
                          key={header.key}
                          {...restHeaderProps}
                        >
                          {header.header}
                        </TableHeader>
                      );
                    })}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!loading && paginatedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={headers.length} style={{ textAlign: 'center', padding: '2rem' }}>
                        {activeFilterCount > 0 || searchTerm
                          ? 'No results found. Try adjusting your search or filters.' 
                          : 'No transaction data found.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((row) => {
                      const rowProps = getRowProps({ row });
                      const { key: rowKey, ...restRowProps } = rowProps;
                      return (
                        <TableRow 
                          key={row.id}
                          {...restRowProps}
                          
                        >
                          <TableCell>{row.propertySeqId || "N/P"}</TableCell>
                          <TableCell>
                            {row.propertyTitle || "N/A"} / <strong>{row.propertyType || "N/A"}</strong>
                          </TableCell>
                          <TableCell>{row.userName || "N/P"}</TableCell>
                          <TableCell>{getVerificationStatusTag(row.verificationStatus)}</TableCell>
                          <TableCell>
                            <Tag type={getValidTagType(getStatusTagType(row.status))}>
                              {row.status || "N/P"}
                            </Tag>
                          </TableCell>
                          <TableCell>
                            {formatCurrency(row.amount, row.currency)}
                          </TableCell>         
                          <TableCell>
                            {row.paymentMethod !== "N/P" && (
                              <Tag type="blue">{row.paymentMethod}</Tag>
                            )}
                            {row.paymentGateway !== "N/P" && (
                              <Tag type="gray" style={{ marginLeft: 6 }}>
                                {row.paymentGateway}
                              </Tag>
                            )}
                            {row.paymentMethod === "N/P" && row.paymentGateway === "N/P" && "N/P"}
                          </TableCell>            
                          <TableCell>
                            <Tag type={getValidTagType(getStatusTagType(row.transactionType))}>
                              {row.transactionType}
                            </Tag>
                          </TableCell>
<TableCell>
  {row.planName ? (
    <div>
      <strong>{row.planName}</strong>
      {row.durationDays && (
        <div style={{ fontSize: "11px", marginTop: "4px", color: "#525252" }}>
          {row.durationDays} days
        </div>
      )}
    </div>
  ) : (
    <span style={{ color: "#8d8d8d" }}>—</span>
  )}
</TableCell>

{/* ✅ New cell for Created At */}
<TableCell>
  {row.createdAt ? (
    <div style={{ fontSize: "13px" }}>
      {formatDateTime(row.createdAt)}
    </div>
  ) : (
    <span style={{ color: "#8d8d8d" }}>N/A</span>
  )}
</TableCell>

                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }}
      </DataTable>

      {/* Pagination */}
      {filteredRows.length > 0 && !loading && (
        <div style={{ padding: "1rem 1.25rem", display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            totalItems={filteredRows.length}
            page={page}
            pageSize={pageSize}
            pageSizes={[100, 50, 20, 10, 5]}
            onChange={({ page: p, pageSize: ps }) => { 
              setPage(p); 
              setPageSize(ps); 
            }}
          />
        </div>
      )}

     
    </>
  );
}